<script setup lang="ts">
import type {PropType} from "vue";
import {autoUpdate, computePosition, offset, flip} from '@floating-ui/vue';

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [String, Object, Number],
    default: '',
  },
  id: {
    type: String,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  optionsPlaceholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  hint: {
    type: [String, Array, undefined],
    default: undefined,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  selectOptions: {
    type: Array as PropType<Array<any>>,
    default: () => []
  },
  selectOptionsKey: {
    type: String,
    default: 'id'
  },
  selectOptionsLabel: {
    type: String,
    default: 'name'
  },
  clearOnSelect: {
    type: Boolean,
    default: true,
  },
  showClear: {
    type: Boolean,
    default: false,
  },
  minHeight: {
    type: String,
    default: '7.5rem'
  },
  enableTogglePassword: {
    type: Boolean,
    default: true,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  minValue: {
    type: Number,
    default: -Infinity
  },
  maxValue: {
    type: Number,
    default: Infinity
  }
});

const computedId = ref(props.id || null)
const placeholderVal = computed(() =>
  showOptions.value ? null : props.placeholder
)

onMounted(() => {
  if (!computedId.value) {
    computedId.value = `${(props.label || 'input').replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  if(isSelect.value) {
    document.addEventListener('click', handleClickOutside);
  }
});

const emit = defineEmits([
  'update:modelValue',
  'search-change',
  'change',
  'blur',
  'clear'
]);

function updateValue(value) {
  emit('update:modelValue', value);
}

const clearValue = () => {
  updateValue('')
  emit('clear');
}

function updateCounterValue(value) {
  emit('update:modelValue', value);
  emit('change', value);
}

//region Select
const selectContainerRef = ref<HTMLElement | null>(null);
const selectInputRef = ref<HTMLElement | null>(null);
const selectOptionsRef = ref<HTMLElement | null>(null);
const floatingStyles = ref({});
let cleanupAutoUpdate = undefined;
watchEffect(async () => {
  if (selectInputRef.value && selectOptionsRef.value) {
    await nextTick();
    cleanupAutoUpdate?.();

    cleanupAutoUpdate = autoUpdate(
        selectInputRef.value,
        selectOptionsRef.value,
        async () => {
          const { x, y } = await computePosition(selectInputRef.value, selectOptionsRef.value, {
            placement: 'bottom-start',
            middleware: [offset(4), flip({ padding: 100 })]
          })
          floatingStyles.value = {
            left: `${x}px`,
            top: `${y}px`,
            position: 'absolute'
          }
        }
    )
  }
});
onUnmounted(() => {
  cleanupAutoUpdate?.();
  if(isSelect.value) {
    document.removeEventListener('click', handleClickOutside);
  }
})

const showOptions = ref(false);
const revertSearchToSelected = () => {
  if (props.clearOnSelect) {
    selectSearch.value = null;
  } else if (props.modelValue) {
    selectSearch.value = props.modelValue[props.selectOptionsLabel];
  } else {
    selectSearch.value = null;
  }
  searching.value = false;
}

const onEscapeKey = () => {
  if (showOptions.value) {
    showOptions.value = false;
    cleanupAutoUpdate?.();
    revertSearchToSelected();
    selectInputRef.value?.blur();
  }
}
const toggleShowOptions = () => {
  if(props.disabled) {
    return;
  }
  if(showOptions.value){
    cleanupAutoUpdate?.();
    revertSearchToSelected();
  } else {
    selectSearch.value = null;
    searching.value = true;
  }
  showOptions.value = !showOptions.value;
}
const selectSearch = ref<string|null>(props.clearOnSelect ? null : (props.modelValue? props.modelValue[props.selectOptionsLabel] : null));
const searching = ref(false);
const filteredSelectOptions = computed(() => {
  return props.selectOptions.filter(option => {
    if(selectSearch.value && searching.value) {
      return option[props.selectOptionsLabel].toLowerCase().includes(selectSearch.value.toLowerCase());
    } else {
      return true;
    }
  });
})
const onSelectOptionClick = (option) => {
  if(props.modelValue && props.modelValue[props.selectOptionsKey] === option[props.selectOptionsKey]) {
    updateValue(null);
  } else {
    updateValue(option);
  }
  if(props.clearOnSelect) {
    selectSearch.value = null
  } else {
    selectSearch.value = option ? option[props.selectOptionsLabel] : null;
    if(selectSearch.value) {
      searching.value = false;
    }
  }

  showOptions.value = false;
  cleanupAutoUpdate?.();
  selectInputRef.value?.blur();
}
const handleClickOutside = (event: MouseEvent) => {
  if(showOptions.value && selectContainerRef.value && !selectContainerRef.value.contains(event.target as HTMLElement)) {
    showOptions.value = false;
    cleanupAutoUpdate?.();
    revertSearchToSelected();
  }
}
const onDropdownClick = () => {
  if(props.disabled) {
    return;
  }
  showOptions.value = true;
  selectSearch.value = null;
  searching.value = true;
}

const isSelect = computed(() => props.type === 'select');

watch(props, () => {
  if (!isSelect.value || searching.value) {
    return;
  }
  if(props.modelValue && props.modelValue[props.selectOptionsLabel] !== selectSearch.value) {
    selectSearch.value = props.modelValue[props.selectOptionsLabel];
  }
}, {deep: true});

const selectedValueKey = computed(() => {
  if (isSelect.value && props.modelValue) {
    return (props.modelValue as Record<string, any>)[props.selectOptionsKey];
  }
  return null;
});
const showTogglePassword = computed(() => {
  return props.enableTogglePassword && props.type === 'password';
});

let showPassword = ref(false);

const inputType = computed(() => {
  if(props.type === 'counter') {
    return 'number'
  }
  return (props.type === 'password' && showPassword.value) ? 'text' : props.type;
});

//endregion

//region watchers

watch(selectSearch, () => {
  emit('search-change', selectSearch.value)
})

//endregion

</script>

<template>
  <div class="wad4-input-group" :class="{ 'wad4-input-group--invalid': invalid, 'wad4-input-group--inline': inline }">

    <label class="wad4-input-group__label" :class="{'wad4-input-group__label--inline': inline}" :for="computedId" v-if="label">
      <span>{{ label }}</span>
      <span class="wad4-input-group__label-required">{{ required ? '*' : '' }}</span>
    </label>

    <div class="wad4-input-group__field" :class="{'wad4-input-group__field--inline': inline}">
      <div v-if="type !== 'textarea' && type !== 'select'" class="wad4-input-group__input_container" :class="{'wad4-input-group__input_container--counter': type === 'counter'}">
        <input class="wad4-input-group__input"
               :class="{'wad4-input-group__input--with-clear' : showClear, 'wad4-input-group__input--counter': type === 'counter'}"
               :id="computedId" :type="inputType" :placeholder="placeholder" :value="modelValue" :disabled="disabled"
               :required="required" @input="updateValue($event.target.value)" @blur="emit('blur', $event.target.value)" />
        <svg v-if="showTogglePassword" class="wad4-input-group__input-icon" @pointerup="showPassword = !showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path v-show="showPassword" d="M12 5.99922C15.79 5.99922 19.17 8.12922 20.82 11.4992C20.23 12.7192 19.4 13.7692 18.41 14.6192L19.82 16.0292C21.21 14.7992 22.31 13.2592 23 11.4992C21.27 7.10922 17 3.99922 12 3.99922C10.73 3.99922 9.51 4.19922 8.36 4.56922L10.01 6.21922C10.66 6.08922 11.32 5.99922 12 5.99922ZM10.93 7.13922L13 9.20922C13.57 9.45922 14.03 9.91922 14.28 10.4892L16.35 12.5592C16.43 12.2192 16.49 11.8592 16.49 11.4892C16.5 9.00922 14.48 6.99922 12 6.99922C11.63 6.99922 11.28 7.04922 10.93 7.13922ZM2.01 3.86922L4.69 6.54922C3.06 7.82922 1.77 9.52922 1 11.4992C2.73 15.8892 7 18.9992 12 18.9992C13.52 18.9992 14.98 18.7092 16.32 18.1792L19.74 21.5992L21.15 20.1892L3.42 2.44922L2.01 3.86922ZM9.51 11.3692L12.12 13.9792C12.08 13.9892 12.04 13.9992 12 13.9992C10.62 13.9992 9.5 12.8792 9.5 11.4992C9.5 11.4492 9.51 11.4192 9.51 11.3692ZM6.11 7.96922L7.86 9.71922C7.63 10.2692 7.5 10.8692 7.5 11.4992C7.5 13.9792 9.52 15.9992 12 15.9992C12.63 15.9992 13.23 15.8692 13.77 15.6392L14.75 16.6192C13.87 16.8592 12.95 16.9992 12 16.9992C8.21 16.9992 4.83 14.8692 3.18 11.4992C3.88 10.0692 4.9 8.88922 6.11 7.96922Z" fill="currentColor"/>
          <path v-show="!showPassword" d="M12 6C15.79 6 19.17 8.13 20.82 11.5C19.17 14.87 15.79 17 12 17C8.21 17 4.83 14.87 3.18 11.5C4.83 8.13 8.21 6 12 6ZM12 4C7 4 2.73 7.11 1 11.5C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 11.5C21.27 7.11 17 4 12 4ZM12 9C13.38 9 14.5 10.12 14.5 11.5C14.5 12.88 13.38 14 12 14C10.62 14 9.5 12.88 9.5 11.5C9.5 10.12 10.62 9 12 9ZM12 7C9.52 7 7.5 9.02 7.5 11.5C7.5 13.98 9.52 16 12 16C14.48 16 16.5 13.98 16.5 11.5C16.5 9.02 14.48 7 12 7Z" fill="currentColor"/>
        </svg>
        <svg v-if="showClear" class="wad4-input-group__input-icon" @pointerup="clearValue" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#d1d1d1"></path>
        </svg>
        <template v-if="type === 'counter'">
          <button class="wad4-input-group__input-button" @click="updateCounterValue(parseInt(modelValue) - 1)" :disabled="disabled || modelValue <= minValue" type="button">
            <svg class="wad4-input-field-counter__svg" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 11.25C19.4142 11.25 19.75 11.5858 19.75 12C19.75 12.4142 19.4142 12.75 19 12.75H5C4.58579 12.75 4.25 12.4142 4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H19Z" fill="currentColor"/>
            </svg>
          </button>
          <button class="wad4-input-group__input-button" @click="updateCounterValue(parseInt(modelValue) + 1)" :disabled="disabled || modelValue >= maxValue" type="button">
            <svg class="wad4-input-field-counter__svg" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M11.25 19.5V12.75H4.5C4.08579 12.75 3.75 12.4142 3.75 12C3.75 11.5858 4.08579 11.25 4.5 11.25H11.25V4.5C11.25 4.08579 11.5858 3.75 12 3.75C12.4142 3.75 12.75 4.08579 12.75 4.5V11.25H19.5C19.9142 11.25 20.25 11.5858 20.25 12C20.25 12.4142 19.9142 12.75 19.5 12.75H12.75V19.5C12.75 19.9142 12.4142 20.25 12 20.25C11.5858 20.25 11.25 19.9142 11.25 19.5Z" fill="currentColor"/>
            </svg>
          </button>
        </template>
      </div>

      <textarea v-if="type === 'textarea'" class="wad4-input-group__input wad4-input-group__textarea" :style="{'min-height': minHeight}"
                :id="computedId" :placeholder="placeholder" :disabled="disabled" :required="required" :value="modelValue"
                @input="updateValue($event.target.value)"/>

      <div v-if="isSelect" class="wad4-input-group__select" ref="selectContainerRef">
        <input :id="computedId" ref="selectInputRef" type="text" :placeholder="placeholderVal" :disabled="disabled" v-model="selectSearch"
               class="wad4-input-group__input wad4-input-group__input--select"
               @pointerup="onDropdownClick" @focusin="onDropdownClick" @keydown="searching = true"
               @keydown.esc="onEscapeKey"/>
        <svg @pointerup="toggleShowOptions" class="wad4-input-group__select-icon" xmlns="http://www.w3.org/2000/svg" height="24px" width="24px" viewBox="0 -960 960 960">
          <path v-show="!showOptions" d="M480-360 280-560h400L480-360Z"/>
          <path v-show="showOptions" d="m280-400 200-200 200 200H280Z"/>
        </svg>
        <Teleport to="body">
          <ul v-if="showOptions" ref="selectOptionsRef" class="wad4-input-group__select-options" :style="floatingStyles">
            <li v-for="option in filteredSelectOptions" class="wad4-input-group__select-options-item"
                :class="{'wad4-input-group__select-options-item--selected': selectedValueKey === option[props.selectOptionsKey]}"
                @pointerup="onSelectOptionClick(option)">
              {{option.name}}
            </li>
            <li v-show="optionsPlaceholder && !selectSearch && filteredSelectOptions.length === 0"
                class="wad4-input-group__select-options-item wad4-input-group__select-options-item--no-results">
              {{ optionsPlaceholder }}
            </li>
            <li v-show="selectSearch && filteredSelectOptions.length === 0"
                class="wad4-input-group__select-options-item wad4-input-group__select-options-item--no-results">
              No results for your search
            </li>
          </ul>
        </Teleport>
      </div>

      <div v-if="hint && !Array.isArray(hint)" class="wad4-input-group__hint" v-html="hint"></div>
      <div v-else-if="hint" class="wad4-input-group__hint">
      <span v-for="token in hint">
        <a v-if="token.isLink" href="#" @pointerup.prevent="contactSupport">{{ token.text }}</a>
        <span v-else>{{ token.text }}</span>
      </span>
      </div>
    </div>

  </div>
</template>

<style lang="scss">
.wad4-input-group {
  display: flex;
  flex-direction: column;
  gap: $wad4-spacing-md;

  &--inline {
    flex-direction: row;
    align-items: flex-start;
    column-gap: $wad4-spacing-2xl;
    row-gap: $wad4-spacing-md;
    flex-wrap: wrap;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-md;

    &--inline {
      flex: 1;
      gap: $wad4-spacing-xs;
    }
  }

  &__label {
    @include text-size-mixin('xs');
    font-weight: $wad4-font-weight-semi-bold;
    display: flex;
    align-items: flex-start;
    gap: $wad4-spacing-xxs;
    color: $wad4-c-text-text-secondary;

    &--inline {
      line-height: 42px;
      @include text-size-mixin('md');
      font-weight: $wad4-font-weight-regular;
      color: $wad4-c-text-text-primary;
      padding-top: 9px;
    }

    &-required {
      color: $wad4-c-foreground-fg-brand-solid;
    }
  }

  &__select {
    position: relative;
    width: 100%;

    &-icon {
      position: absolute;
      right: $wad4-spacing-lg;
      top:$wad4-spacing-md;

      & path {
        fill: $wad4-c-text-text-tertiery;
      }
    }

    &-options {
      background: $wad4-c-background-bg-primary;
      list-style: none;
      padding: $wad4-spacing-md $wad4-spacing-lg;
      margin: 0;
      max-height: calc(200px + (2 * $wad4-spacing-md));
      overflow-y: scroll;
      z-index: 1003;
      min-width: 370px;
      border: solid 1px $wad4-c-border-border-primary;
      box-shadow: $wad4-box-shadow-sm;
      border-radius: $wad4-border-radius-xs;

      @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
        min-width: 0;
        width: calc(100% - (2 * $wad4-container-spacing) - 12px);
      }

      &-item {
        color: $wad4-c-text-text-primary;
        overflow: hidden;
        text-overflow: ellipsis;
        @include text-size-mixin('md');
        padding: $wad4-spacing-md;
        cursor: default;

        &:hover {
          background: $wad4-c-background-bg-tertiery;
        }

        &--selected {
          background: $wad4-c-background-bg-brand-solid;
          color: $wad4-c-text-text-primary-on-brand;

          &:hover {
            background: $wad4-c-background-bg-brand-solid;
          }
        }

        &--no-results {
          font-style: italic;
          color: $wad4-c-text-text-disabled;

          &:hover {
            background: none;
          }
        }
      }
    }
  }

  &__input {
    font-family: $wad4-default-font-sans;
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-xs;
    background: $wad4-c-background-bg-primary;
    box-shadow: 0 1px 2px 0 $wad4-bc-shadow-neutral-5;
    color: $wad4-c-text-text-primary;
    @include text-size-mixin('md');
    padding: $wad4-spacing-md $wad4-spacing-lg;
    width: 100%;

    &--select {
      padding-right: calc($wad4-spacing-lg + 20px);

      &::placeholder {
        color: $wad4-c-text-text-placeholder;
      }

      &:disabled::placeholder {
        color: $wad4-c-text-text-disabled;
      }
    }

    &--counter {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;

      /* Hide spinners on Chrome, Safari, Edge, Opera */
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      /* Hide spinners on Firefox */
      &[type=number] {
        -moz-appearance: textfield;
      }
    }

    &:focus {
      outline: 2px solid $wad4-c-border-border-brand;
      outline-offset: -2px;
    }

    &:disabled {
      background: $wad4-c-background-bg-disabled;
      color: $wad4-c-text-text-disabled;
    }

    &--with-clear {
      padding: $wad4-spacing-md $wad4-spacing-5xl $wad4-spacing-md $wad4-spacing-lg;
    }

    &_container {
      position: relative;
      width: 100%;
      box-shadow: 0 1px 2px 0 $wad4-bc-shadow-neutral-5;

      &--counter {
        display: flex;
      }
    }

    &-icon {
      cursor: pointer;
      position: absolute;
      top: 9px;
      right: $wad4-spacing-lg;
    }

    &-button {
      display: flex;
      padding: $wad4-spacing-md;
      justify-content: center;
      align-items: center;
      gap: $wad4-spacing-xs;
      border-radius: 0;
      border-top: 1px solid $wad4-c-border-border-primary;
      border-bottom: 1px solid $wad4-c-border-border-primary;
      border-left: unset;
      border-right: 1px solid $wad4-c-border-border-primary;
      background: $wad4-c-background-bg-primary;
      color: $wad4-c-components-buttons-secondary-gray-button-secondary-gray-fg;

      &:last-child {
        border-radius: 0 $wad4-border-radius-xs $wad4-border-radius-xs 0;
      }

      &:disabled {
        background: $wad4-c-background-bg-disabled;
      }
    }
  }

  &__textarea{
    resize: vertical;
    font-family: $wad4-default-font-sans;
  }

  &__hint {
    @include text-size-mixin('xs');
    color: $wad4-c-text-text-tertiery;
  }

  &--invalid {
    .wad4-input-group__input {
      outline: 2px solid $wad4-c-border-border-error;
      outline-offset: -2px;
    }

    .wad4-input-group__hint {
      color: $wad4-c-text-text-error;
    }
  }
}
</style>
